import axiosInstance from './axiosInstance';
import storage from 'local-storage-fallback';
import { AUTH } from 'src/constants';
import { AxiosRequestConfig } from 'axios';
import { captureException } from 'src/observability';

interface RequesProps extends AxiosRequestConfig {
  rawResponse?: boolean;
  /**
   * Opt in to the bounded 503 retry for idempotent reads sent as non-GET
   * requests (e.g. ES reads that POST a large query-params body). Unlike RTK
   * Query, the axios layer has no read/write signal, so mutations must NOT set
   * this. See MKT-293.
   */
  retryOn503?: boolean;
}

export const request = async ({
  url,
  data = undefined,
  method = 'GET',
  headers = {},
  timeout = 120_000,
  cancelToken = null,
  responseType = 'json',
  rawResponse = false,
  retryOn503 = false,
}: RequesProps) => {
  let accessToken;
  let refreshToken;

  try {
    accessToken = JSON.parse(storage.getItem(AUTH.ACCESS_TOKEN));
    refreshToken = JSON.parse(storage.getItem(AUTH.REFRESH_TOKEN));
  } catch (err) {
    // if the token is not a valid json, it's legacy
    accessToken = storage.getItem(AUTH.ACCESS_TOKEN);
    refreshToken = storage.getItem(AUTH.REFRESH_TOKEN);
  }

  const options = {
    headers: {
      Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      'Refresh-Token': refreshToken,
      ...headers,
    },
    timeout,
    method,
    data,
    url,
    cancelToken,
    responseType,
    retryOn503,
  };

  try {
    const response = await axiosInstance.request(options);

    const headers = response?.headers || {};
    const newAccessToken = headers?.['x-access-token'];
    const newRefreshToken = headers?.['x-refresh-token'];

    // if these tokens are present, its a new refresh and access token to be saved
    if (newAccessToken && newRefreshToken) {
      storage.setItem(AUTH.ACCESS_TOKEN, JSON.stringify(newAccessToken));
      storage.setItem(AUTH.REFRESH_TOKEN, JSON.stringify(newRefreshToken));
    }

    // Temporary for Sam
    if (response?.status && response?.status === 403) {
      console.error('403 Error Thrown', {
        response,
        url,
      });
    }

    return rawResponse || response?.status === 205
      ? response
      : (response && response?.data && response?.data?.result) ||
          response?.data;
  } catch (err) {
    captureException(err);
    throw err.response?.data || err;
  }
};

export const instance = axiosInstance;
